import { useEffect, useState } from 'react';
import { useExplorerStore } from './explorerStore';
import { useSharedStore } from '../stores/sharedStore';
import { AtlasView } from './AtlasView';
import { WorldView } from './WorldView';
import type { Book } from '../lib/api/types';

/**
 * AI 探索模式（Phase 2 Week 7-8 · Ticket 01）
 * 从双模式共享状态层（sharedStore）读取 books/currentBook/booksLoading/bookLoading/booksError/bookError，
 * 避免与 3D 阅读器重复加载。Atlas 星图（书库） ⇄ World 世界（单本书页面节点探索）双视图。
 */
export default function AiExplorer() {
  const { view, selectedBookSlug, enterAtlas } = useExplorerStore();
  const { books, currentBook, booksLoading, bookLoading, booksError, bookError, loadBooks, loadBook } = useSharedStore();
  const [localError, setLocalError] = useState<string | null>(null);

  // 首次挂载时加载书库（已加载则跳过，避免重复）
  useEffect(() => {
    if (books.length === 0) {
      loadBooks().catch((err: any) => setLocalError(err.message ?? '书库加载失败'));
    }
  }, [books.length, loadBooks]);

  // 进入世界视图时加载书籍详情（若 sharedStore 中已有且 slug 匹配则跳过）
  useEffect(() => {
    if (view === 'world' && selectedBookSlug) {
      if (!currentBook || currentBook.slug !== selectedBookSlug) {
        loadBook(selectedBookSlug).catch((err: any) => setLocalError(err.message ?? '绘本详情加载失败'));
      }
    }
  }, [view, selectedBookSlug, currentBook, loadBook]);

  const displayError = booksError || bookError || localError;
  const isLoading =
    (view === 'atlas' && books.length === 0 && booksLoading) ||
    (view === 'world' && selectedBookSlug && (!currentBook || currentBook.slug !== selectedBookSlug) && bookLoading);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {/* 模式切换：AI 探索 ⇄ 3D 阅读器 */}
      <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 50, display: 'flex', gap: '8px' }}>
        <a
          href={`/?mode=reader${selectedBookSlug ? `&book=${selectedBookSlug}` : ''}`}
          style={{
            background: 'rgba(10,12,28,0.72)', border: '1px solid #ffffff33', color: '#fff',
            borderRadius: '18px', padding: '8px 16px', textDecoration: 'none', fontSize: '13px',
            backdropFilter: 'blur(10px)',
          }}
        >
          ◀ 3D 阅读器
        </a>
        {view === 'world' && (
          <button
            onClick={enterAtlas}
            style={{
              background: 'rgba(10,12,28,0.72)', border: '1px solid #ffffff33', color: '#fff',
              borderRadius: '18px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px',
              backdropFilter: 'blur(10px)',
            }}
          >
            ✦ 返回星图
          </button>
        )}
      </div>

      {isLoading && (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'system-ui, sans-serif', fontSize: '15px' }}>
          正在探索星空…
        </div>
      )}

      {!isLoading && displayError && (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'system-ui, sans-serif', gap: '14px' }}>
          <div style={{ fontSize: '32px' }}>🌌</div>
          <div style={{ opacity: 0.75, fontSize: '14px' }}>{displayError}</div>
          <a href="/?mode=library" style={{ color: '#4ade80', fontSize: '14px' }}>前往绘本图书馆</a>
        </div>
      )}

      {!isLoading && !displayError && view === 'atlas' && <AtlasView books={books} onSelect={(slug) => useExplorerStore.getState().enterBook(slug)} />}

      {!isLoading && !displayError && view === 'world' && selectedBookSlug && currentBook && (
        <WorldView book={currentBook as Book} onExit={enterAtlas} />
      )}
    </div>
  );
}
